/**
 * Authentication and authorization utilities for the Admin UI
 *
 * This module provides functions to check user permissions based on
 * feature flags embedded in JWT tokens.
 */

interface TokenPayload {
  payload: {
    type: string;
    access: string;
    user: {
      sessionUuid: string;
      uuid: string;
      isAccountLocked: boolean;
      source: string;
      datacenters: string[];
      isSource: boolean;
      emailAddress: string;
      lastLogin: string;
      hasPassword: boolean;
      needsPasswordReset: boolean;
      features: string[];
    };
  };
  iat: number;
  exp: number;
  aud: string[];
  iss: string;
}

/**
 * Decodes a JWT token without verification (client-side only)
 * Note: This is for reading claims only, not for security validation
 */
function decodeToken(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Checks if the token is an app token (not a user token)
 * App tokens have type 'application' and don't have user features
 *
 * @param token - JWT token
 * @returns true if token is an app token
 */
export function isAppToken(token: string): boolean {
  if (!token) return false;

  const decoded = decodeToken(token);
  return decoded?.payload?.type === 'application';
}

/**
 * Gets the features array from the current user's token
 */
function getFeatures(token: string): string[] {
  const decoded = decodeToken(token);
  return decoded?.payload?.user?.features ?? [];
}

/**
 * Checks if the current user has a specific feature flag
 *
 * @param featureName - The feature flag to check (e.g., 'impersonate_users')
 * @param token - Optional JWT token. If not provided, will attempt to get from tokenService
 * @returns true if the user has the feature, false otherwise
 *
 * @example
 * ```typescript
 * if (hasFeature('impersonate_users', token)) {
 *   // Show impersonate button
 * }
 * ```
 */
export function hasFeature(featureName: string, token: string): boolean {
  if (!token) {
    console.warn('No token provided to hasFeature check');
    return false;
  }

  const features = getFeatures(token);
  return features.includes(featureName);
}

/**
 * Checks if the current user has ANY of the specified features
 *
 * @param featureNames - Array of feature flags to check
 * @param token - JWT token
 * @returns true if the user has at least one of the features
 */
export function hasAnyFeature(featureNames: string[], token: string): boolean {
  if (!token) return false;

  const features = getFeatures(token);
  return featureNames.some((feature) => features.includes(feature));
}

/**
 * Checks if the current user has ALL of the specified features
 *
 * @param featureNames - Array of feature flags to check
 * @param token - JWT token
 * @returns true if the user has all of the features
 */
export function hasAllFeatures(featureNames: string[], token: string): boolean {
  if (!token) return false;

  const features = getFeatures(token);
  return featureNames.every((feature) => features.includes(feature));
}

/**
 * Gets all features for the current user
 *
 * @param token - JWT token
 * @returns Array of feature flags
 */
export function getAllFeatures(token: string): string[] {
  return getFeatures(token);
}

/**
 * Feature flag constants for type safety
 */
export const Features = {
  IMPERSONATE_USERS: 'impersonate_users',
  ADMIN_CREATE_EDIT_ROLE: 'admin_create_edit_role',
  RESET_USER_PASSWORD: 'reset_user_password',
  RECONCILE_USERS: 'reconcile_users',
  REMOVE_MULTIPLE_USERS: 'remove_multiple_users',
  ADMIN_PARTNERS: 'admin_partners',
  BILLING_SUPER_ADMIN: 'billing_super_admin',
  BUYER_MARKET_SETTINGS_ADMIN: 'buyer_market_settings_admin',
  DATACENTER_TOGGLE: 'datacenter_toggle',
  MANUAL_AWARD_INVOICES: 'manual_award_invoices',
  PROXY_OFFER: 'proxy_offer',
  SRM_HOME_ACCESS: 'srm_home_access',
} as const;
